// Which bundle this browser is running.
//
// The Worker stamps the build hash into the <script> tag it serves
// (`main.js?v=...`), so the running module can read its own version off its own
// URL. Nothing has to be injected at build time and the two cannot disagree:
// the string in the tag *is* the string the asset server was asked for.

/** The version of the bundle currently executing, or "dev" when the page was
 *  served without a stamp. */
export const BUILD_VERSION = new URL(import.meta.url).searchParams.get("v") ?? "dev";

/** Whether a reload has already been spent this tab.
 *
 *  A deploy that somehow never satisfies the comparison — a cached document
 *  pointing at a bundle that no longer exists, say — would otherwise put the
 *  tab in a reload loop, which is a far worse failure than running a stale
 *  client. One reload per tab, and then it gives up and plays. */
const RELOAD_MARK = "mtf:reloaded-for";

/** Reload if the world is running a different build than this page.
 *
 *  The world says which bundle it belongs to in its `welcome`, and the only
 *  moment this is checked is that message: the player has just connected, they
 *  are standing still, and the server is authoritative for everything, so a
 *  reload here costs a second and loses nothing. Left unchecked, a deploy
 *  leaves every open tab talking an old protocol to a new world until somebody
 *  happens to press refresh.
 *
 *  Returns whether it decided to reload, so a caller can stop what it is doing.
 */
export function reloadIfStale(worldVersion: string, storage = safeSessionStorage()): boolean {
  if (!worldVersion || worldVersion === "dev" || BUILD_VERSION === "dev") return false;
  if (worldVersion === BUILD_VERSION) return false;
  if (storage?.getItem(RELOAD_MARK) === worldVersion) return false;
  storage?.setItem(RELOAD_MARK, worldVersion);
  location.reload();
  return true;
}

/** Session storage, or nothing. A browser with storage disabled still plays;
 *  it simply does not get the loop guard, and the version check is idempotent
 *  enough that one extra reload is the whole cost. */
function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
